// src/client/renderer/character/_internals.ts
//
// Shared helpers for character rig GPU-resource management.
// Used by both assembler.ts and head-swap.ts to avoid code duplication.

import * as THREE from 'three';

/**
 * Traverse `scene` and return the first THREE.Mesh encountered, or null if none.
 *
 * Pure traversal — does not mutate the scene.
 */
export function findFirstMesh(scene: THREE.Group): THREE.Mesh | null {
  let firstMesh: THREE.Mesh | null = null;
  scene.traverse((child) => {
    if (firstMesh === null && child instanceof THREE.Mesh) {
      firstMesh = child;
    }
  });
  return firstMesh;
}

/**
 * Dispose a mesh's owned geometry and material(s), then detach it from its parent.
 * Idempotent on null / undefined input.
 *
 * NOTE: this helper does NOT dispose textures (mesh.material.map / .normalMap / etc).
 * When the material is created via `material.clone()` (which we do in head-swap, etc.),
 * THREE.Material.clone() SHALLOW-copies texture references — those textures are shared
 * with the GLTFLoader's cache and other clones. Disposing them here would corrupt the
 * cache and break other consumers. The asset loader / GLTFLoader cache teardown is
 * responsible for texture lifecycle.
 */
export function disposeMesh(mesh: THREE.Mesh | null | undefined): void {
  if (mesh == null) return;

  mesh.geometry.dispose();

  const mat = mesh.material;
  if (Array.isArray(mat)) {
    for (const m of mat) {
      (m as THREE.Material).dispose();
    }
  } else {
    (mat as THREE.Material).dispose();
  }

  mesh.removeFromParent();
}
